import { Plus } from "lucide-react";
import React, { useEffect } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";

import type { MedVisitDetailResponse } from "../../../types/medchron";
import { SkeletonView } from "./SkeletonView";
import { VisitCard, type VisitCardSharedProps } from "./VisitCard";
import { VisitGroupHeader } from "./VisitGroupHeader";
import { VisitsBulkSelectionInlineActions } from "./VisitsBulkSelectionInlineActions";
import { ChronologyViewType, type VisitDisplay, type VisitsFlatItem } from "./types";

export interface VisitsVirtualizedListProps extends VisitCardSharedProps {
  flatItems: VisitsFlatItem[];
  groupVisitsByKey: Map<string, VisitDisplay[]>;
  visitRowCheckboxState: (visit: VisitDisplay) => {
    checked: boolean;
    indeterminate: boolean;
  };
  onVisitRowBulkToggle: (visit: VisitDisplay) => void;
  groupHeaderCheckboxState: (groupVisits: VisitDisplay[]) => {
    checked: boolean;
    indeterminate: boolean;
  };
  onGroupHeaderBulkToggle: (groupKey: string) => void;
  selectedVisitCount: number;
  isBulkActionBusy?: boolean;
  onBulkEditClick?: () => void;
  onBulkCopyClick?: () => void;
  onBulkDeleteClick?: () => void;
  hasOtherVersions?: boolean;
  isVisitsLoading?: boolean;
  virtuosoRef?: React.RefObject<VirtuosoHandle | null>;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  viewType: ChronologyViewType;
  expandedGroups: Record<string, boolean>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  getVisitDetails: (visit: VisitDisplay) => MedVisitDetailResponse[];
  onNewVisitClick: () => void;
  highlightQuery: string;
  targetVisitId?: string | null;
}

export const VisitsVirtualizedList: React.FC<VisitsVirtualizedListProps> = ({
  flatItems,
  groupVisitsByKey,
  visitRowCheckboxState,
  onVisitRowBulkToggle,
  groupHeaderCheckboxState,
  onGroupHeaderBulkToggle,
  selectedVisitCount,
  isBulkActionBusy,
  onBulkEditClick,
  onBulkCopyClick,
  onBulkDeleteClick,
  hasOtherVersions,
  isVisitsLoading,
  virtuosoRef,
  scrollContainerRef,
  viewType,
  expandedGroups,
  setExpandedGroups,
  getVisitDetails,
  onNewVisitClick,
  highlightQuery,
  targetVisitId,
  editMode,
  template,
  ...visitCardRest
}) => {
  useEffect(() => {
    if (!targetVisitId) return;
    const index = flatItems.findIndex(
      (item) => item.kind === "visit" && item.visit.visit_id === targetVisitId
    );
    if (index === -1) return;
    virtuosoRef?.current?.scrollToIndex({ index, align: "center", behavior: "auto" });
  }, [targetVisitId, flatItems, virtuosoRef]);

  return (
    <div className="visits-container pt-2">
      {editMode && (
        <div className="pointer-events-none sticky right-1 top-1 z-10 flex h-0 flex-wrap items-center justify-end gap-2 overflow-visible">
          {selectedVisitCount > 0 && (
            <>
              <div className="pointer-events-auto">
                <VisitsBulkSelectionInlineActions
                  selectedVisitCount={selectedVisitCount}
                  variant="actionBar"
                  isBusy={isBulkActionBusy}
                  onBulkEditClick={onBulkEditClick}
                  onBulkCopyClick={onBulkCopyClick}
                  onBulkDeleteClick={onBulkDeleteClick}
                  hasOtherVersions={hasOtherVersions}
                />
              </div>
              <span className={`hidden h-8 w-px bg-neutral-200 sm:block`} aria-hidden />
            </>
          )}

          <button
            type="button"
            onClick={onNewVisitClick}
            className="hover:bg-info-hover pointer-events-auto flex shrink-0 items-center gap-2 rounded-xl bg-info px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:shadow-md active:scale-press"
          >
            <Plus size={18} strokeWidth={2.5} /> New Visit
          </button>
        </div>
      )}
      {isVisitsLoading ? (
        <SkeletonView />
      ) : (
        <Virtuoso
          ref={virtuosoRef}
          customScrollParent={scrollContainerRef?.current ?? undefined}
          useWindowScroll={!scrollContainerRef?.current}
          totalCount={flatItems.length}
          defaultItemHeight={280}
          increaseViewportBy={{ top: 600, bottom: 600 }}
          computeItemKey={(index) => {
            const item = flatItems[index];
            if (!item) return index;
            return item.kind === "header" ? item.groupKey : item.visit.visit_id;
          }}
          itemContent={(index) => {
            const item = flatItems[index];
            if (!item) return null;
            if (item.kind === "header") {
              const groupVisits = groupVisitsByKey.get(item.groupKey) ?? [];
              const isGroupedChronologyView = viewType !== ChronologyViewType.DATE;
              const groupBulk = groupHeaderCheckboxState(groupVisits);
              return (
                <VisitGroupHeader
                  groupKey={item.groupKey}
                  groupLabel={item.groupLabel}
                  visitCount={item.visitCount}
                  viewType={viewType}
                  template={template}
                  isExpanded={expandedGroups[item.groupKey] ?? viewType === ChronologyViewType.DATE}
                  onToggle={() =>
                    setExpandedGroups((prev) => ({
                      ...prev,
                      [item.groupKey]: !prev[item.groupKey],
                    }))
                  }
                  showGroupBulkCheckbox={editMode && isGroupedChronologyView}
                  groupBulkCheckboxChecked={groupBulk.checked}
                  groupBulkCheckboxIndeterminate={groupBulk.indeterminate}
                  onGroupBulkCheckboxToggle={() => onGroupHeaderBulkToggle(item.groupKey)}
                />
              );
            }
            const { visit } = item;
            const visitBulk = visitRowCheckboxState(visit);
            return (
              <div className="py-1.5">
                <VisitCard
                  {...visitCardRest}
                  editMode={editMode}
                  template={template}
                  visit={visit}
                  details={getVisitDetails(visit)}
                  visitBulkSelectChecked={visitBulk.checked}
                  visitBulkSelectIndeterminate={visitBulk.indeterminate}
                  onVisitBulkSelectToggle={() => onVisitRowBulkToggle(visit)}
                  highlightQuery={highlightQuery}
                  isFlashTarget={visit.visit_id === targetVisitId}
                />
              </div>
            );
          }}
        />
      )}
    </div>
  );
};
